/**
 * PAGINATION
 * Composant réutilisable pour la pagination des listes
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Select } from '../ui';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  displayedItems?: number; // Nombre d'items affichés sur la page courante
  onItemsPerPageChange?: (itemsPerPage: number) => void; // Optionnel
  itemsPerPageOptions?: number[]; // Options disponibles (ex: [10, 25, 50, 100])
}

export function Pagination({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
}: PaginationProps) {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  function handlePrevious() {
    if (canGoPrevious) {
      onPageChange(currentPage - 1);
    }
  }

  function handleNext() {
    if (canGoNext) {
      onPageChange(currentPage + 1);
    }
  }

  return (
    <div className="flex items-center justify-between px-page-padding py-4">
      {/* Bouton Précédent - À gauche */}
      <button
        onClick={handlePrevious}
        disabled={!canGoPrevious}
        className="flex items-center justify-center gap-2 h-9 px-4 py-2 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="size-4" />
        <span className="text-sm font-medium text-zinc-900">Précédent</span>
      </button>

      {/* Centre : Sélecteur + Compteur */}
      <div className="flex items-center gap-3">
        {/* Sélecteur items par page (minimaliste) */}
        {onItemsPerPageChange && (
          <Select
            variant="inline"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="font-medium cursor-pointer"
            aria-label="Nombre d'éléments par page"
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        )}

        {/* Compteur */}
        <p className="text-sm font-medium text-zinc-900">
          {startItem}-{endItem} <span className="text-zinc-500 font-normal">sur {totalItems}</span>
        </p>
      </div>

      {/* Bouton Suivant - À droite */}
      <button
        onClick={handleNext}
        disabled={!canGoNext}
        className="flex items-center justify-center gap-2 h-9 px-4 py-2 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="text-sm font-medium text-zinc-900">Suivant</span>
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
}
